import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TambaLogo from "@/components/TambaLogo";
import { 
  ArrowRight, 
  ArrowLeft,
  DollarSign,
  PiggyBank,
  CreditCard,
  Target,
  TrendingUp,
  Briefcase,
  Check,
  Sparkles,
  Loader2,
  Calendar
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    monthly_income: "",
    income_range: "",
    current_savings: "",
    monthly_expenses: "",
    total_debt: "",
    debt_types: [],
    financial_goals: [],
    risk_tolerance: 50,
    risk_answers: {},
    year_of_birth: "",
    profession: "",
    skills: [],
    years_experience: ""
  });

  // Risk assessment questions
  const riskQuestions = [
    {
      id: "investment_horizon",
      question: "How long do you plan to invest before needing this money?",
      options: [
        { value: "short", label: "Less than 2 years", score: 1 },
        { value: "medium", label: "2-5 years", score: 2 },
        { value: "long", label: "5-10 years", score: 3 },
        { value: "very_long", label: "10+ years", score: 4 }
      ]
    },
    {
      id: "loss_reaction",
      question: "If your investment dropped 20% in value, what would you do?",
      options: [
        { value: "sell_all", label: "Sell everything immediately", score: 1 },
        { value: "sell_some", label: "Sell some to reduce risk", score: 2 },
        { value: "hold", label: "Hold and wait for recovery", score: 3 },
        { value: "buy_more", label: "Buy more at the lower price", score: 4 }
      ]
    },
    {
      id: "investment_knowledge",
      question: "How would you describe your investment knowledge?",
      options: [
        { value: "none", label: "None - I'm completely new", score: 1 },
        { value: "basic", label: "Basic - I know stocks and bonds", score: 2 },
        { value: "good", label: "Good - I understand diversification", score: 3 },
        { value: "expert", label: "Expert - I actively manage portfolios", score: 4 }
      ]
    },
    {
      id: "risk_preference",
      question: "Which best describes your ideal investment?",
      options: [
        { value: "preserve", label: "Low risk, protect my money", score: 1 },
        { value: "balanced", label: "Balanced growth and safety", score: 2 },
        { value: "growth", label: "Higher risk for higher returns", score: 3 },
        { value: "aggressive", label: "Maximum growth, accept volatility", score: 4 }
      ]
    }
  ];

  // Calculate risk score from answers
  const calculateRiskScore = (answers) => {
    const totalQuestions = riskQuestions.length;
    let totalScore = 0;
    
    riskQuestions.forEach(q => {
      const answer = answers[q.id];
      if (answer) {
        const option = q.options.find(o => o.value === answer);
        if (option) totalScore += option.score;
      }
    });
    
    // Convert to 0-100 scale
    const maxScore = totalQuestions * 4;
    const percentage = Math.round((totalScore / maxScore) * 100);
    return percentage;
  };

  // Generate year options (18-100 years old)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 83 }, (_, i) => currentYear - 18 - i);

  const incomeRanges = [
    { value: "0-2000", label: "Under $2,000", icon: "💰" },
    { value: "2000-5000", label: "$2,000 - $5,000", icon: "💵" },
    { value: "5000-10000", label: "$5,000 - $10,000", icon: "💎" },
    { value: "10000-20000", label: "$10,000 - $20,000", icon: "🏆" },
    { value: "20000+", label: "$20,000+", icon: "👑" }
  ];

  const savingsRanges = [
    { value: "0-1000", label: "Under $1,000", color: "bg-red-100 border-red-300 text-red-700" },
    { value: "1000-5000", label: "$1,000 - $5,000", color: "bg-yellow-100 border-yellow-300 text-yellow-700" },
    { value: "5000-20000", label: "$5,000 - $20,000", color: "bg-tamba-blue/10 border-tamba-blue/30 text-tamba-blue" },
    { value: "20000-50000", label: "$20,000 - $50,000", color: "bg-tamba-teal/10 border-tamba-teal/30 text-tamba-teal" },
    { value: "50000+", label: "$50,000+", color: "bg-green-100 border-green-300 text-green-700" }
  ];

  const expenseRanges = [
    { value: "0-1000", label: "Under $1,000" },
    { value: "1000-2500", label: "$1,000 - $2,500" },
    { value: "2500-5000", label: "$2,500 - $5,000" },
    { value: "5000-10000", label: "$5,000 - $10,000" },
    { value: "10000+", label: "$10,000+" }
  ];

  const debtTypes = [
    { value: "none", label: "No Debt", icon: "✨" },
    { value: "credit_cards", label: "Credit Cards", icon: "💳" },
    { value: "student_loans", label: "Student Loans", icon: "🎓" },
    { value: "car_loan", label: "Car Loan", icon: "🚗" },
    { value: "mortgage", label: "Mortgage", icon: "🏠" },
    { value: "personal_loans", label: "Personal Loans", icon: "📝" }
  ];

  const financialGoals = [
    { value: "emergency_fund", label: "Build Emergency Fund", icon: "🛡️", color: "border-tamba-blue" },
    { value: "pay_debt", label: "Pay Off Debt", icon: "💳", color: "border-red-400" },
    { value: "save_home", label: "Save for Home", icon: "🏠", color: "border-tamba-teal" },
    { value: "retirement", label: "Retirement Savings", icon: "🏖️", color: "border-tamba-yellow" },
    { value: "invest", label: "Start Investing", icon: "📈", color: "border-green-500" },
    { value: "travel", label: "Travel Fund", icon: "✈️", color: "border-purple-500" },
    { value: "education", label: "Education/Skills", icon: "📚", color: "border-indigo-500" },
    { value: "business", label: "Start Business", icon: "🚀", color: "border-tamba-coral" }
  ];

  const skillCategories = [
    { value: "technology", label: "Technology/IT", icon: "💻" },
    { value: "finance", label: "Finance/Accounting", icon: "📊" },
    { value: "marketing", label: "Marketing/Sales", icon: "📣" },
    { value: "creative", label: "Creative/Design", icon: "🎨" },
    { value: "healthcare", label: "Healthcare", icon: "🏥" },
    { value: "education", label: "Education/Teaching", icon: "📚" },
    { value: "trades", label: "Skilled Trades", icon: "🔧" },
    { value: "management", label: "Management/Leadership", icon: "👔" }
  ];

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    
    // Check if user already completed onboarding
    if (userData.onboarding_completed) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const steps = [
    {
      id: "welcome",
      title: "Welcome to Tamba! 👋",
      subtitle: "Let's personalize your financial journey",
      icon: Sparkles
    },
    {
      id: "age",
      title: "When were you born?",
      subtitle: "Your age helps us recommend appropriate investments",
      icon: Calendar
    },
    {
      id: "income",
      title: "What's your monthly income?",
      subtitle: "This helps us understand your earning power",
      icon: DollarSign
    },
    {
      id: "savings",
      title: "How much do you have saved?",
      subtitle: "Including all savings accounts and emergency funds",
      icon: PiggyBank
    },
    {
      id: "expenses",
      title: "What are your monthly expenses?",
      subtitle: "Rent, utilities, groceries, subscriptions, etc.",
      icon: CreditCard
    },
    {
      id: "debt",
      title: "Do you have any debt?",
      subtitle: "Select all that apply",
      icon: CreditCard
    },
    {
      id: "goals",
      title: "What are your financial goals?",
      subtitle: "Select up to 3 priorities",
      icon: Target
    },
    {
      id: "risk",
      title: "Let's assess your risk tolerance",
      subtitle: "Answer these questions to get personalized investment advice",
      icon: TrendingUp
    },
    {
      id: "profession",
      title: "Tell us about your career",
      subtitle: "This helps us suggest earning opportunities",
      icon: Briefcase
    },
    {
      id: "complete",
      title: "You're all set! 🎉",
      subtitle: "Let's start building your wealth",
      icon: Check
    }
  ];

  const progress = ((currentStep) / (steps.length - 1)) * 100;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSelectOption = (field, value, isMultiple = false, maxSelections = null) => {
    if (isMultiple) {
      setFormData(prev => {
        const current = prev[field] || [];
        if (current.includes(value)) {
          return { ...prev, [field]: current.filter(v => v !== value) };
        } else {
          if (maxSelections && current.length >= maxSelections) {
            toast.info(`You can select up to ${maxSelections} options`);
            return prev;
          }
          return { ...prev, [field]: [...current, value] };
        }
      });
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      // Auto advance for single select
      setTimeout(handleNext, 300);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Save onboarding data to backend
      await axios.post(`${API}/onboarding/${user.id}`, formData);
      
      // Update local user data
      const updatedUser = { ...user, onboarding_completed: true, ...formData };
      localStorage.setItem("tamba_user", JSON.stringify(updatedUser));
      
      toast.success("Profile setup complete! Let's build your wealth.");
      navigate("/dashboard");
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error("Failed to save profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    const step = steps[currentStep];
    
    switch (step.id) {
      case "welcome":
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-8"
          >
            <TambaLogo className="h-24 w-24 mx-auto mb-6" />
            <h2 className="font-heading text-3xl font-bold mb-4">Welcome, {user?.name || "Friend"}!</h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
              In the next few minutes, we'll learn about your financial situation to give you personalized advice.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {["📊 Personalized Insights", "💡 Smart Recommendations", "🎯 Goal Tracking"].map((item, i) => (
                <span key={i} className="px-4 py-2 bg-secondary rounded-full text-sm">
                  {item}
                </span>
              ))}
            </div>
            <Button 
              size="lg" 
              onClick={handleNext}
              className="rounded-full px-10 py-6 text-lg bg-tamba-blue hover:bg-tamba-blue/90"
              data-testid="start-onboarding-btn"
            >
              Let's Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        );

      case "age":
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6 py-4"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-tamba-blue/10 mb-4">
                <Calendar className="w-10 h-10 text-tamba-blue" />
              </div>
              <p className="text-muted-foreground">
                Your age helps us recommend age-appropriate investment strategies
              </p>
            </div>
            
            <div className="max-w-xs mx-auto">
              <Label className="text-base mb-3 block text-center">Year of Birth</Label>
              <Select 
                value={formData.year_of_birth} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, year_of_birth: value }))}
              >
                <SelectTrigger className="h-14 text-lg rounded-xl" data-testid="year-of-birth-select">
                  <SelectValue placeholder="Select your birth year" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {yearOptions.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year} ({currentYear - year} years old)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={handleNext}
              className="w-full mt-8 rounded-full py-6 bg-tamba-blue hover:bg-tamba-blue/90"
              disabled={!formData.year_of_birth}
              data-testid="age-continue-btn"
            >
              Continue
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        );

      case "income":
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div className="grid gap-3">
              {incomeRanges.map((range) => (
                <button
                  key={range.value}
                  onClick={() => handleSelectOption("income_range", range.value)}
                  className={`p-5 rounded-2xl border-2 text-left transition-all hover:scale-[1.02] ${
                    formData.income_range === range.value
                      ? "border-tamba-blue bg-tamba-blue/10 shadow-lg"
                      : "border-border hover:border-tamba-blue/50"
                  }`}
                  data-testid={`income-${range.value}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{range.icon}</span>
                    <div>
                      <p className="font-semibold text-lg">{range.label}</p>
                      <p className="text-sm text-muted-foreground">per month</p>
                    </div>
                    {formData.income_range === range.value && (
                      <Check className="ml-auto h-6 w-6 text-tamba-blue" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        );

      case "savings":
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div className="grid gap-3">
              {savingsRanges.map((range) => (
                <button
                  key={range.value}
                  onClick={() => handleSelectOption("current_savings", range.value)}
                  className={`p-5 rounded-2xl border-2 text-left transition-all hover:scale-[1.02] ${
                    formData.current_savings === range.value
                      ? `${range.color} shadow-lg`
                      : "border-border hover:border-tamba-blue/50"
                  }`}
                  data-testid={`savings-${range.value}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-lg">{range.label}</p>
                    {formData.current_savings === range.value && (
                      <Check className="h-6 w-6" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        );

      case "expenses":
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div className="grid gap-3">
              {expenseRanges.map((range) => (
                <button
                  key={range.value}
                  onClick={() => handleSelectOption("monthly_expenses", range.value)}
                  className={`p-5 rounded-2xl border-2 text-left transition-all hover:scale-[1.02] ${
                    formData.monthly_expenses === range.value
                      ? "border-tamba-coral bg-tamba-coral/10 shadow-lg"
                      : "border-border hover:border-tamba-coral/50"
                  }`}
                  data-testid={`expenses-${range.value}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-lg">{range.label}</p>
                    {formData.monthly_expenses === range.value && (
                      <Check className="h-6 w-6 text-tamba-coral" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        );

      case "debt":
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-3">
              {debtTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => {
                    if (type.value === "none") {
                      setFormData(prev => ({ ...prev, debt_types: ["none"] }));
                    } else {
                      handleSelectOption("debt_types", type.value, true);
                    }
                  }}
                  className={`p-4 rounded-2xl border-2 text-center transition-all hover:scale-[1.02] ${
                    formData.debt_types?.includes(type.value)
                      ? type.value === "none" 
                        ? "border-green-500 bg-green-50 shadow-lg"
                        : "border-tamba-coral bg-tamba-coral/10 shadow-lg"
                      : "border-border hover:border-tamba-blue/50"
                  }`}
                  data-testid={`debt-${type.value}`}
                >
                  <span className="text-3xl mb-2 block">{type.icon}</span>
                  <p className="font-medium text-sm">{type.label}</p>
                </button>
              ))}
            </div>
            <Button 
              onClick={handleNext}
              className="w-full mt-6 rounded-full py-6 bg-tamba-blue hover:bg-tamba-blue/90"
              disabled={!formData.debt_types?.length}
            >
              Continue
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        );

      case "goals":
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <p className="text-center text-muted-foreground mb-4">
              Selected: {formData.financial_goals?.length || 0}/3
            </p>
            <div className="grid grid-cols-2 gap-3">
              {financialGoals.map((goal) => (
                <button
                  key={goal.value}
                  onClick={() => handleSelectOption("financial_goals", goal.value, true, 3)}
                  className={`p-4 rounded-2xl border-2 text-center transition-all hover:scale-[1.02] ${
                    formData.financial_goals?.includes(goal.value)
                      ? `${goal.color} bg-opacity-10 shadow-lg border-2`
                      : "border-border hover:border-tamba-blue/50"
                  }`}
                  data-testid={`goal-${goal.value}`}
                >
                  <span className="text-3xl mb-2 block">{goal.icon}</span>
                  <p className="font-medium text-sm">{goal.label}</p>
                </button>
              ))}
            </div>
            <Button 
              onClick={handleNext}
              className="w-full mt-6 rounded-full py-6 bg-tamba-blue hover:bg-tamba-blue/90"
              disabled={!formData.financial_goals?.length}
            >
              Continue
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        );

      case "risk":
        const answeredQuestions = Object.keys(formData.risk_answers || {}).length;
        const allQuestionsAnswered = answeredQuestions === riskQuestions.length;
        const currentRiskScore = calculateRiskScore(formData.risk_answers || {});
        const riskLabel = currentRiskScore < 30 ? "Conservative" : 
                         currentRiskScore < 60 ? "Moderate" : "Aggressive";
        
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {riskQuestions.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`w-3 h-3 rounded-full transition-all ${
                    idx < answeredQuestions ? "bg-tamba-blue" : "bg-secondary"
                  }`}
                />
              ))}
            </div>

            {/* Questions */}
            {riskQuestions.map((q, qIdx) => (
              <motion.div
                key={q.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: qIdx * 0.1 }}
                className="space-y-3"
              >
                <p className="font-medium text-sm">
                  {qIdx + 1}. {q.question}
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {q.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        const newAnswers = { ...formData.risk_answers, [q.id]: option.value };
                        const newScore = calculateRiskScore(newAnswers);
                        setFormData(prev => ({ 
                          ...prev, 
                          risk_answers: newAnswers,
                          risk_tolerance: newScore
                        }));
                      }}
                      className={`p-3 rounded-xl border-2 text-left text-sm transition-all ${
                        formData.risk_answers?.[q.id] === option.value
                          ? "border-tamba-blue bg-tamba-blue/10"
                          : "border-border hover:border-tamba-blue/50"
                      }`}
                      data-testid={`risk-${q.id}-${option.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{option.label}</span>
                        {formData.risk_answers?.[q.id] === option.value && (
                          <Check className="h-4 w-4 text-tamba-blue" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            ))}

            {/* Result Preview */}
            {allQuestionsAnswered && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 rounded-2xl bg-gradient-to-br from-tamba-blue/10 to-tamba-teal/10 border border-tamba-blue/20"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                    currentRiskScore < 30 ? "bg-green-100" :
                    currentRiskScore < 60 ? "bg-tamba-yellow/20" : "bg-tamba-coral/20"
                  }`}>
                    <span className="text-2xl">
                      {currentRiskScore < 30 ? "🛡️" : currentRiskScore < 60 ? "⚖️" : "🚀"}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Your Risk Profile</p>
                    <p className="font-heading text-xl font-bold">{riskLabel}</p>
                    <p className="text-xs text-muted-foreground">
                      {currentRiskScore < 30 
                        ? "Focus on capital preservation and stable returns"
                        : currentRiskScore < 60 
                        ? "Balance between growth and stability"
                        : "Higher risk tolerance for potentially higher returns"}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            <Button 
              onClick={handleNext}
              className="w-full rounded-full py-6 bg-tamba-blue hover:bg-tamba-blue/90"
              disabled={!allQuestionsAnswered}
            >
              {allQuestionsAnswered ? "Continue" : `Answer all ${riskQuestions.length} questions`}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        );

      case "profession":
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <Label className="text-base mb-3 block">Your Profession</Label>
              <Input
                placeholder="e.g., Software Engineer, Teacher, Nurse..."
                value={formData.profession}
                onChange={(e) => setFormData(prev => ({ ...prev, profession: e.target.value }))}
                className="h-14 text-lg rounded-xl"
                data-testid="profession-input"
              />
            </div>

            <div>
              <Label className="text-base mb-3 block">Your Skill Areas</Label>
              <div className="grid grid-cols-2 gap-3">
                {skillCategories.map((skill) => (
                  <button
                    key={skill.value}
                    onClick={() => handleSelectOption("skills", skill.value, true)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      formData.skills?.includes(skill.value)
                        ? "border-tamba-teal bg-tamba-teal/10"
                        : "border-border hover:border-tamba-teal/50"
                    }`}
                    data-testid={`skill-${skill.value}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{skill.icon}</span>
                      <span className="font-medium text-sm">{skill.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-base mb-3 block">Years of Experience</Label>
              <div className="grid grid-cols-4 gap-2">
                {["0-2", "3-5", "6-10", "10+"].map((range) => (
                  <button
                    key={range}
                    onClick={() => setFormData(prev => ({ ...prev, years_experience: range }))}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      formData.years_experience === range
                        ? "border-tamba-blue bg-tamba-blue/10"
                        : "border-border hover:border-tamba-blue/50"
                    }`}
                    data-testid={`experience-${range}`}
                  >
                    <span className="font-medium">{range}</span>
                    <span className="text-xs text-muted-foreground block">years</span>
                  </button>
                ))}
              </div>
            </div>

            <Button 
              onClick={handleNext}
              className="w-full rounded-full py-6 bg-tamba-blue hover:bg-tamba-blue/90"
              disabled={!formData.profession}
            >
              Complete Setup
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        );

      case "complete":
        const finalRiskScore = formData.risk_tolerance || 50;
        const finalRiskLabel = finalRiskScore < 30 ? "Conservative" : 
                              finalRiskScore < 60 ? "Moderate" : "Aggressive";
        const userAge = formData.year_of_birth ? currentYear - parseInt(formData.year_of_birth) : null;
        
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="font-heading text-3xl font-bold mb-4">Profile Complete!</h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
              We've analyzed your financial profile and have personalized recommendations ready for you.
            </p>
            
            <div className="bg-secondary/50 rounded-2xl p-6 mb-8 text-left max-w-md mx-auto">
              <h3 className="font-semibold mb-4">Your Financial Snapshot:</h3>
              <div className="space-y-2 text-sm">
                {userAge && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Age:</span>
                    <span className="font-medium">{userAge} years old</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monthly Income:</span>
                  <span className="font-medium">{formData.income_range || "Not set"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current Savings:</span>
                  <span className="font-medium">{formData.current_savings || "Not set"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Top Goals:</span>
                  <span className="font-medium">{formData.financial_goals?.length || 0} selected</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Risk Profile:</span>
                  <span className={`font-medium px-2 py-1 rounded-full text-xs ${
                    finalRiskScore < 30 ? "bg-green-100 text-green-700" :
                    finalRiskScore < 60 ? "bg-tamba-yellow/20 text-tamba-yellow" :
                    "bg-tamba-coral/20 text-tamba-coral"
                  }`}>
                    {finalRiskLabel}
                  </span>
                </div>
              </div>
            </div>

            <Button 
              size="lg"
              onClick={handleComplete}
              disabled={loading}
              className="rounded-full px-12 py-6 text-lg bg-tamba-blue hover:bg-tamba-blue/90"
              data-testid="complete-onboarding-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </motion.div>
        );

      default:
        return null;
    }
  };

  const currentStepData = steps[currentStep];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with progress */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/30 px-4 py-3">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <TambaLogo className="h-10 w-auto" />
            {currentStep > 0 && currentStep < steps.length - 1 && (
              <span className="text-sm text-muted-foreground">
                Step {currentStep} of {steps.length - 2}
              </span>
            )}
          </div>
          {currentStep > 0 && currentStep < steps.length - 1 && (
            <Progress value={progress} className="h-2" />
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Step header */}
              {currentStep > 0 && currentStep < steps.length - 1 && (
                <div className="text-center mb-8">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 ${
                    currentStep === 1 ? "bg-tamba-blue/10" :
                    currentStep === 2 ? "bg-tamba-teal/10" :
                    currentStep === 3 ? "bg-tamba-coral/10" :
                    currentStep === 4 ? "bg-red-100" :
                    currentStep === 5 ? "bg-tamba-yellow/10" :
                    currentStep === 6 ? "bg-purple-100" :
                    "bg-secondary"
                  }`}>
                    <currentStepData.icon className={`w-8 h-8 ${
                      currentStep === 1 ? "text-tamba-blue" :
                      currentStep === 2 ? "text-tamba-teal" :
                      currentStep === 3 ? "text-tamba-coral" :
                      currentStep === 4 ? "text-red-500" :
                      currentStep === 5 ? "text-tamba-yellow" :
                      currentStep === 6 ? "text-purple-500" :
                      "text-muted-foreground"
                    }`} />
                  </div>
                  <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-2">
                    {currentStepData.title}
                  </h1>
                  <p className="text-muted-foreground">{currentStepData.subtitle}</p>
                </div>
              )}

              {/* Step content */}
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer navigation */}
      {currentStep > 0 && currentStep < steps.length - 1 && (
        <footer className="sticky bottom-0 bg-background/80 backdrop-blur-xl border-t border-border/30 px-4 py-4">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="gap-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="text-muted-foreground"
              data-testid="skip-btn"
            >
              Skip for now
            </Button>
          </div>
        </footer>
      )}
    </div>
  );
};

export default OnboardingPage;
